import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import { setLanguage } from "vanilla-cookieconsent";
import {
  InitialCookieConsent,
  type InitialCookieConsentProps,
  isConsentBannerInitialized,
  resolveConsentLocale,
} from "@ahead-health/core/ui/cookieConsent";
import { parseMarketFromUrl } from "@/config/market";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import "@ahead-health/core/ui/cookieConsent/overrides.css";

/** Supported cookie-consent banner-copy languages. */
const CC_LANGUAGES = new Set(["en", "de", "fr", "nl"]);

/** `surface` picks the processors the preferences modal discloses — the
 * booking app and apps/membership-first share this banner. */
export default function CookieConsentBanner({
  surface = "booking",
}: {
  surface?: InitialCookieConsentProps["surface"];
}) {
  const { i18n } = useTranslation();
  const raw = (i18n.language || "en").toLowerCase().split("-")[0];
  const base = CC_LANGUAGES.has(raw) ? raw : "en";

  // i18n only carries the base language (en/de/fr); Germany-vs-Switzerland lives
  // in the market (?country=de). Read it synchronously from the URL — the banner
  // is mount-once, so the store's async-hydrated market isn't ready yet — and
  // upgrade German to de-DE so it gets the TTDSG/DSGVO Impressum + privacy footer.
  const market = parseMarketFromUrl(
    new URLSearchParams(window.location.search),
  );
  // AT rides the same de-DE variant: Austria applies the GDPR plus its own
  // TKG §165 (the local ePrivacy transposition), so the fuller German banner
  // is the safer of the two we have. Revisit if AT ever needs its own copy.
  const locale =
    base === "de" && (market === "de" || market === "at") ? "de-DE" : base;

  // The banner is set up once, in the language of the first render; a
  // language picked after that (?locale=de applied once i18n loads, or the
  // switcher) moves its copy too.
  // ponytail: a language picked while the geo lookup is still in flight
  // (before the banner exists) is missed; watch CONSENT_UI_MODE_EVENT if that
  // shows up.
  useEffect(() => {
    if (isConsentBannerInitialized()) {
      void setLanguage(resolveConsentLocale(locale));
    }
  }, [locale]);

  return (
    <InitialCookieConsent
      posthog={posthog}
      surface={surface}
      syncMetaPixel
      geoEndpoint="/api/geo"
      locale={locale}
    />
  );
}
